/**
 * EPIC-DM01-D: Demo Results Hook
 * Fetches and manages demo analysis results for display
 */

import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DemoInsight {
  title: string;
  description: string;
  severity?: 'low' | 'medium' | 'high';
}

export interface DrillRecommendation {
  id?: string;
  name: string;
  description?: string;
  duration?: string;
}

export interface DemoViewModel {
  // ── Existing fields (keep as-is for backward compat) ──────────────
  top2Insights: DemoInsight[];
  focusArea: {
    title: string;
    description: string;
  };
  recommendedDrill: DrillRecommendation | null;
  overallScore?: number;
  prospectName?: string;

  // ── NEW: Expanded fields for Figma Results redesign ───────────────
  /** Full coaching summary paragraph from the analysis engine */
  coachingSummary?: string;
  /** e.g. "rotational", "compensated" */
  swingClassification?: string;
  /** Internal pattern key, e.g. "early_extension" — not displayed raw */
  primaryPattern?: string;
  /** Qualitative metric map, e.g. { posture_rise: "excessive", pelvis_depth: "excessive" } */
  qualitativeMetrics?: Record<string, string>;
  /** From impact_if_addressed */
  impactOnGame?: string;
  /** e.g. "2-4 weeks" */
  fixTimeline?: string;
  /** e.g. "3x per week" */
  practiceFrequency?: string;
  /** e.g. "15 min" */
  practiceMinutes?: string;
  /** Up to 2 swing thoughts */
  swingThoughts?: string[];
  /** Structured drill recommendations from the coaching engine */
  drills?: Array<{
    title: string;
    why: string;
    steps?: string[];
    sets_reps?: string;
    primary_feel?: string;
  }>;
  /** Practice plan overview text */
  trainingPlanSummary?: string;
  /** Root cause analysis causal chain */
  rootCauses?: Array<{
    title: string;
    relationship: string;
    evidence: string;
  }>;
  /** Root cause summary text */
  rootCauseSummary?: string;
  /** Weekly practice schedule */
  weeklySchedule?: Array<{
    session: string;
    drills: string[];
  }>;
  /** Expected outcomes + secondary benefits */
  benefits?: string[];
  /** Derived severity: High / Medium / Low */
  severity?: string;
  /** Why this is the primary focus */
  primaryFocusDetail?: string;
}

export interface DemoResultsResponse {
  viewModel: DemoViewModel;
  playbackUrl: string;
  shareEligible: boolean;
  faultFrameUrl: string | null;
  run_id: string;
}

export interface DemoStatusResponse {
  stage: 'uploading' | 'processing' | 'pose' | 'analysis' | 'ready' | 'failed';
  progress: number;
  statusText: string;
  run_id: string;
  errorMessage?: string;
}

// API helper
async function getAccessToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return session.access_token;
}

async function callEdgeFunction<T>(
  functionName: string,
  method: 'GET' | 'POST' = 'GET',
  body?: unknown,
  params?: URLSearchParams
): Promise<T> {
  const token = await getAccessToken();
  
  let url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;
  if (params) {
    url += `?${params.toString()}`;
  }

  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const data = await response.json();
  
  if (!response.ok) {
    const error = new Error(data.error || `Request failed with status ${response.status}`);
    (error as any).code = data.code;
    (error as any).stage = data.stage;
    throw error;
  }

  return data as T;
}

export function useDemoResults(sessionId: string) {
  const [privacyMode, setPrivacyMode] = useState(false);

  // Fetch results
  const {
    data: results,
    isLoading,
    error,
    refetch,
  } = useQuery<DemoResultsResponse>({
    queryKey: ['demo-results', sessionId],
    queryFn: async () => {
      const params = new URLSearchParams({ demo_session_id: sessionId });
      return callEdgeFunction<DemoResultsResponse>('demo-results', 'GET', undefined, params);
    },
    enabled: !!sessionId,
    retry: 1,
    staleTime: 60_000, // 1 minute
  });

  // Toggle privacy mode (hides details)
  const togglePrivacyMode = useCallback(() => {
    setPrivacyMode(prev => !prev);
    
    // Emit telemetry
    console.log('[useDemoResults] Privacy toggle:', {
      event: 'demo_privacy_toggle_used',
      session_id: sessionId,
      enabled: !privacyMode,
    });
  }, [sessionId, privacyMode]);

  // Track CTA clicks
  const trackActivateClick = useCallback(() => {
    console.log('[useDemoResults] Activate CTA clicked:', {
      event: 'demo_cta_activate_clicked',
      session_id: sessionId,
    });
  }, [sessionId]);

  const trackShareClick = useCallback(() => {
    console.log('[useDemoResults] Share clicked:', {
      event: 'demo_share_clicked',
      session_id: sessionId,
    });
  }, [sessionId]);

  return {
    // Data
    results,
    viewModel: results?.viewModel ?? null,
    playbackUrl: results?.playbackUrl ?? null,
    shareEligible: results?.shareEligible ?? false,
    faultFrameUrl: results?.faultFrameUrl ?? null,
    
    // State
    isLoading,
    error: error as Error | null,
    privacyMode,
    
    // Actions
    refetch,
    togglePrivacyMode,
    trackActivateClick,
    trackShareClick,
  };
}

/**
 * Hook for polling processing status
 */
export function useDemoProcessingStatus(
  sessionId: string, 
  options: { 
    enabled?: boolean; 
    pollingInterval?: number;
    onComplete?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { 
    enabled = true, 
    pollingInterval = 3000,
    onComplete,
    onError,
  } = options;

  const [isComplete, setIsComplete] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const {
    data: status,
    isLoading,
    error,
    refetch,
  } = useQuery<DemoStatusResponse>({
    queryKey: ['demo-run-status', sessionId],
    queryFn: async () => {
      const params = new URLSearchParams({ demo_session_id: sessionId });
      return callEdgeFunction<DemoStatusResponse>('demo-run-status', 'GET', undefined, params);
    },
    enabled: enabled && !!sessionId && !isComplete && !isFailed,
    refetchInterval: enabled && !isComplete && !isFailed ? pollingInterval : false,
    retry: 2,
  });

  // Handle status changes
  useEffect(() => {
    if (status?.stage === 'ready') {
      setIsComplete(true);
      onComplete?.();
    } else if (status?.stage === 'failed') {
      setIsFailed(true);
      onError?.(new Error(status.errorMessage || 'Analysis failed'));
    }
  }, [status?.stage, status?.errorMessage, onComplete, onError]);

  return {
    status,
    stage: status?.stage ?? 'processing',
    progress: status?.progress ?? 0,
    statusText: status?.statusText ?? 'Processing...',
    isLoading,
    error: error as Error | null,
    errorMessage: status?.stage === 'failed' ? (status.errorMessage ?? null) : null,
    isComplete,
    isFailed,
    refetch,
  };
}
